import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { LoggingService } from '@logging/logging.service';
import { isImportTypeAvailableForOrganisation } from '@app/bulk-import/hub/bulk-import-hub-catalog';
import { ImportContextId } from '@app/bulk-import/schemas/import-schema';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ActiveSecurityContextStateService } from '../active-security/active-security-context.state-service';

@Injectable({ providedIn: 'root' })
export class BulkImportPayrollUserRouteGuard implements CanActivate {
  constructor(
    private router: Router,
    private authenticationState: ActiveAuthenticationStateService,
    private securityStateService: ActiveSecurityContextStateService,
    private logger: LoggingService
  ) {}

  canActivate(next: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.securityStateService.fetchingActiveUser$.pipe(
      filter((fetching) => fetching === false),
      take(1),
      map(() => {
        if (!this.authenticationState.isAuthenticated) {
          return false;
        }

        // Wizard routes name their import type; the hub doesn't. Block types this organisation doesn't
        // have, so a direct URL can't reach an import the hub deliberately hides.
        const importContext = next.data['importContext'] as ImportContextId | undefined;
        if (importContext && !isImportTypeAvailableForOrganisation(importContext, this.securityStateService)) {
          this.logger.trace(`BulkImportPayrollUserRouteGuard: '${importContext}' import not available for this organisation`);
          return this.router.parseUrl('forbidden');
        }

        const mode = next.data['mode'];

        if (mode === 'group') {
          if (!this.securityStateService.hasOrganisationAuthorityTo('AccessGroups')) {
            this.logger.trace('BulkImportPayrollUserRouteGuard: non-group user blocked from group bulk import');
            return this.router.parseUrl('forbidden');
          }
          return true;
        }

        // The hub is open to group users too (unlike the payroll-scoped routes below), so this guard
        // simply allows it; the import-type permission is enforced by OrganisationAuthorisedRouteGuard
        // on the route (BULK_IMPORT_PERMISSIONS), which does the idiomatic forbidden/no-permissions mapping.
        if (mode === 'hub') {
          return true;
        }

        if (this.securityStateService.isGroupUser()) {
          this.logger.trace('BulkImportPayrollUserRouteGuard: group user blocked from bulk import');
          return this.router.parseUrl('forbidden');
        }

        return true;
      })
    );
  }
}
